import { Button, Text } from "@/components/ui"
import { TRANSITION } from "@/lib/motion"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type { CSSProperties } from "react"
import { CouponArt } from "./CouponArt"

const NOTCHES = "radial-gradient(circle at 0 50%, transparent 5px, black 5.5px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 50%, transparent 5px, black 5.5px) right / 51% 100% no-repeat"

const TICKET_MASK: CSSProperties = { WebkitMask: NOTCHES, mask: NOTCHES }

const STATUS_TONE = { success: "game-accent", pending: "inherit", warning: "warning" } as const

type CouponTicketButtonProps = {
  /** The visible name: the benefit ("10% OFF até R$ 15") or, without one, the code (drawn monospace). */
  title: string
  mono: boolean
  status: string
  tone: keyof typeof STATUS_TONE
  label: string
  onClick: () => void
}

/** A small ink ticket naming a coupon and where it stands ("10% OFF · faltam R$ 12,00"); the status swaps with a short cross-fade. */
export function CouponTicketButton({ title, mono, status, tone, label, onClick }: CouponTicketButtonProps) {
  const reduceMotion = useReducedMotion()
  return (
    <Button variant="solid" tone="game" size="xs" shape="rounded" onClick={onClick} style={TICKET_MASK} aria-label={label} className="max-w-full gap-1.5">
      <CouponArt size={22} />
      <Text as="span" size="xs" weight="bold" tracking={mono ? "wide" : undefined} leading="none" tone="inherit" truncate className={mono ? "min-w-0 font-mono" : "min-w-0"}>
        {title}
      </Text>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={status}
          initial={{ opacity: 0, y: reduceMotion ? 0 : 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: reduceMotion ? 0 : -4, transition: TRANSITION.exit }}
          transition={TRANSITION.size}
          className="shrink-0"
        >
          <Text as="span" size="xs" weight="bold" leading="none" tone={STATUS_TONE[tone]} className="whitespace-nowrap tabular-nums">
            {status}
          </Text>
        </motion.span>
      </AnimatePresence>
    </Button>
  )
}
